import React, { useState } from 'react';
import { Handle, Position } from '@xyflow/react';
import { Database, Loader2, CheckCircle, X, Play } from 'lucide-react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { DataSourceSelector } from '../../data-sources/DataSourceSelector';
import { DataSource } from '../../../types/dataSource';

export const SqlImportNode: React.FC<{ data: { label: string; color: string; isTemplate?: boolean } }> = ({ data }) => {
  const [selectedSource, setSelectedSource] = useState<DataSource | null>(null);
  const [query, setQuery] = useState('');
  const [outputName, setOutputName] = useState('');
  const [status, setStatus] = useState<'idle' | 'importing' | 'done' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');
  const [rowCount, setRowCount] = useState(0);

  const handleImport = async () => {
    if (!selectedSource || !query.trim()) return;
    setStatus('importing');
    setErrorMsg('');
    // SQL execution goes through the sql-executor Lambda
    // For now, show a placeholder that directs to Upload page
    setTimeout(() => {
      setStatus('error');
      setErrorMsg('SQL import via flows coming soon. Use the Upload page SQL tab.');
    }, 1000);
  };

  const reset = () => {
    setQuery('');
    setOutputName('');
    setStatus('idle');
    setErrorMsg('');
    setRowCount(0);
  };

  return (
    <div className="bg-card border rounded-xl shadow-md w-[340px] overflow-hidden">
      <div
        className="px-3 py-2 text-xs font-semibold text-white flex items-center gap-1.5"
        style={{ background: data.color }}
      >
        <Database className="h-3.5 w-3.5" />
        {data.label}
      </div>

      <div className="p-3 space-y-2">
        <DataSourceSelector
          typeFilter={['postgresql', 'mysql', 'sqlserver']}
          selectedSourceId={selectedSource?.source_id || null}
          onSourceSelect={(s) => {
            setSelectedSource(s);
            setStatus('idle');
            setErrorMsg('');
          }}
          compact
          onAddSource={() => window.open('/upload', '_blank')}
        />

        {selectedSource && status === 'idle' && (
          <>
            <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
              <Database className="h-3 w-3 shrink-0" />
              <span className="truncate">{selectedSource.name}</span>
              {selectedSource.config?.database && (
                <span className="text-[10px]">({selectedSource.config.database})</span>
              )}
            </div>
            <div className="space-y-1.5 nodrag">
              <Input
                className="h-7 text-[11px] font-mono"
                placeholder="SELECT * FROM table LIMIT 100"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
              <Input
                className="h-7 text-[11px]"
                placeholder="Output name (optional)"
                value={outputName}
                onChange={(e) => setOutputName(e.target.value)}
              />
            </div>
            <Button
              variant="secondary"
              size="sm"
              className="w-full text-xs h-7"
              onClick={handleImport}
              disabled={!query.trim()}
            >
              <Play className="h-3 w-3 mr-1" />
              Import
            </Button>
          </>
        )}

        {status === 'importing' && (
          <div className="flex items-center gap-2 py-2">
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            <span className="text-xs">Importing...</span>
          </div>
        )}

        {status === 'done' && (
          <div className="flex items-center gap-2">
            <CheckCircle className="h-4 w-4 text-green-500 shrink-0" />
            <span className="text-xs truncate flex-1">
              {outputName || 'import'} ({rowCount.toLocaleString()} rows)
            </span>
            <Button variant="ghost" size="icon" className="h-6 w-6" onClick={reset}>
              <X className="h-3 w-3" />
            </Button>
          </div>
        )}

        {status === 'error' && (
          <div className="space-y-1">
            <p className="text-xs text-destructive">{errorMsg}</p>
            <Button variant="outline" size="sm" className="w-full text-xs h-7" onClick={reset}>
              Try again
            </Button>
          </div>
        )}
      </div>

      <Handle type="source" position={Position.Right} className="!bg-muted-foreground" />
    </div>
  );
};
